import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { ProgressBar } from '@/components/ProgressBar';
import { BackgroundOverlay } from '@/components/Tile';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { VideoDate } from '@/components/VideoDate';
import { translationFormulaIntercept, translationFormulaSlope, translationWorkerActivationTime } from '@/environment';
import DangerCircle2 from '@/images/DangerCircle2.svg';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import { VideoBadge } from '@/pages/home/components/Common';
import { useSelectedTranslationGroups } from '@/pages/translation/hooks';
import { useAnalytics } from '@/services/analytics';
import { MixPanelEvent } from '@/utils/types';
import { ITranslationGroup, TranslationGroupStatus } from '@d-id/types';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { Typography } from '@mui/material';
import { ReactElement, useState } from 'react';
import { getBadgeIcon, getErrorTextByKind, isGroupDraftEditable } from '../../utils';
import { TranslationContainerHover } from '../TranslationContainerHover';

const MiddleContainer = styled.div`
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    color: ${({ theme }) => theme.colors.secondary[900]};
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.32px;
`;

/* 
in case of error state, we are showing a tooltip + we want to maintain 
a clickable menu button, in order to acheive that we are elevating the z index 
of the middle container and shrinking so it will not occupy the whole screen so the menu
will be clickable
*/
const MiddleContainerErrorState = styled(MiddleContainer)`
    width: 50%;
    height: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
`;

const ControlsContainer = styled.div`
    opacity: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 2;
    transition: all 0.2s ease-in-out;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const TranslationErrorTooltipContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const UploadingSpinner = styled(Player)`
    height: 78px;
`;

const UploadingText = styled.span`
    color: white;
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.28px;
`;

const ImageContainer = styled.div<{ hovered: boolean; isItemSelected: boolean; isSelectable: boolean; disableHover: boolean }>`
    border-radius: 12px;
    border: ${({ theme, isItemSelected }) =>
        isItemSelected ? `2px solid ${theme.colors.secondary[900]}` : `1px solid ${theme.colors.black[300]}`};
    cursor: ${({ disableHover }) => (disableHover ? '' : 'pointer')};
    width: 100%;
    aspect-ratio: 16/10;
    position: relative;
    overflow: hidden;

    ${({ hovered }) =>
        hovered &&
        `
        .BackgroundOverlay {
            opacity: 0.5;
            transform: scale(105%);
        }
        
        ${ControlsContainer} {
            opacity: 1;
        }
    `}

    ${({ isSelectable }) =>
        !isSelectable &&
        `
        :hover .BackgroundOverlay {
            opacity: 0.5;
            transform: scale(105%);
        }
    
        :hover ${ControlsContainer} {
            opacity: 1;
        }
    
        :hover .translation-container-hover {
            display: block;
        }
    `}
`;

const InfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 12px;
`;

const VideoTitle = styled.div`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ErrorImage = styled.img`
    width: 40px;
    height: 40px;
`;

const LanguageBadge = styled.div`
    background-color: ${({ theme }) => theme.colors.primary.bgLight};
    padding: 0 8px;
    border-radius: 4px;
    display: flex;
    gap: 8px;
    align-items: center;

    color: ${({ theme }) => theme.colors.primary[950]};
    font: 'Libre Franklin';
    font-size: 12px;
    font-weight: 400;
    text-transform: capitalize;
`;

const ExampleBadge = styled.div`
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 2;

    background-color: white;
    padding: 6px;
    border-radius: 4px;

    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 12px;
    font-weight: 600;
    line-height: 8px; /* 66.667% */
`;

const BadgeContainer = styled.div`
    display: flex;
    gap: 6px;
    z-index: 2;
    position: absolute;
    bottom: 12px;
    left: 12px;
`;

const StyledVideoBadge = styled(VideoBadge)`
    position: unset !important;
`;

const ProofreadBadge = styled(Typography)`
    font-size: 1.2rem;
    color: ${({ theme }) => theme.colors.links.main};
    background-color: ${({ theme }) => theme.colors.links.light};
    line-height: 1.454rem;
    padding: 0.25rem 0.8rem;
    border-radius: 0.4rem;
`;

/**
 * Calculate the processing time based on the duration for translation.
 * @param duration The duration of the video in seconds.
 * @returns The processing time in seconds rounded to one decimal place. Formula: processingTime = slope * duration + intercept
 * @example processingTime = 13.9303 * duration + 13.8923
 */
export const calculateProcessingTime = (duration: number) =>
    Math.round((translationFormulaSlope * duration + translationFormulaIntercept) * 10) / 10 + translationWorkerActivationTime;

interface Props {
    translationGroup: ITranslationGroup;
    isExample?: boolean;
    origin: string;
}

const TranslationGroupCard = ({ translationGroup, isExample, origin }: Props) => {
    const { isSelected, selectMode, onSelect } = useSelectedTranslationGroups();
    const [keepHover, setKeepHover] = useState(false);
    const mixpanel = useAnalytics();

    const onVideoOpen = () => {
        mixpanel?.track('video-preview', {
            event: MixPanelEvent.CLICK,
            from: origin,
            videoId: translationGroup.id,
            editorType: 'editor',
        });
    };

    const formattedDate = new Date(translationGroup.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });

    const isSingleTranslation = translationGroup.target_languages.length === 1;
    const isDraftEditable = isGroupDraftEditable(translationGroup);
    const isProofreading = translationGroup.translations?.[0]?.proofreading;
    const isLoading =
        ![
            TranslationGroupStatus.Uploading,
            TranslationGroupStatus.Validating,
            TranslationGroupStatus.Done,
            TranslationGroupStatus.Error,
            TranslationGroupStatus.PartialDone,
        ].includes(translationGroup.status) && !isDraftEditable;
    const isError = translationGroup.status === TranslationGroupStatus.Error;
    const isUploading = [TranslationGroupStatus.Uploading, TranslationGroupStatus.Validating].includes(translationGroup.status);
    const isDone = [TranslationGroupStatus.Done, TranslationGroupStatus.PartialDone].includes(translationGroup.status);

    const isSelectable = selectMode && !isLoading && !isUploading;

    const isItemSelected = !isUploading && isSelected(translationGroup.id);

    const handleSelect = (e: React.MouseEvent<Element, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(translationGroup);
    };

    const infoContainer = () => (
        <InfoContainer>
            <VideoTitle data-testid={`translation-card-title-${translationGroup.id}`}>{translationGroup.name}</VideoTitle>
            <VideoDate formattedDate={formattedDate} />
        </InfoContainer>
    );

    const badgeContainer = () => (
        <BadgeContainer>
            <LanguageBadge>
                {isSingleTranslation ? (
                    <>
                        {getBadgeIcon(translationGroup.target_languages[0])} {translationGroup.target_languages[0]}
                    </>
                ) : (
                    `🌍 ${translationGroup.target_languages.length} languages`
                )}
            </LanguageBadge>
            {isDraftEditable && <StyledVideoBadge isDraft />}
            {!isDraftEditable && isProofreading && <ProofreadBadge>Proofread</ProofreadBadge>}
            {isDone && (
                <StyledVideoBadge testId={`translation-duration-badge-${translationGroup.id}`} duration={translationGroup.duration} />
            )}
        </BadgeContainer>
    );

    const middleContainer = () => {
        if (isError) {
            return (
                <MiddleContainerErrorState>
                    <CustomTooltip cursor="pointer" placement="top" title={getErrorTextByKind(translationGroup.error_kind)}>
                        <TranslationErrorTooltipContent>
                            <ErrorImage src={DangerCircle2} />
                            Translation failed
                        </TranslationErrorTooltipContent>
                    </CustomTooltip>
                </MiddleContainerErrorState>
            );
        }

        if (isLoading) {
            return (
                <MiddleContainer>
                    <ProgressBar
                        duration={translationGroup.duration!}
                        startedAt={new Date(translationGroup.generated_at || translationGroup.created_at).getTime()}
                        calcFormula={calculateProcessingTime}
                        theme="dark"
                        textVariant="text-inside-with-estimation"
                    />
                </MiddleContainer>
            );
        }

        if (isUploading) {
            return (
                <MiddleContainer>
                    <UploadingSpinner src={LoadingAnimation} autoplay loop />
                    <UploadingText>Uploading...</UploadingText>
                </MiddleContainer>
            );
        }

        return null;
    };

    const controlsContainer = () =>
        (isDone || isError || isDraftEditable) && (
            <ControlsContainer>
                <TranslationContainerHover
                    isEditable={isDraftEditable}
                    showPlay={!(isError || isDraftEditable)}
                    translationGroup={translationGroup}
                    isExample={isError ? undefined : isExample}
                    keepHover={keepHover}
                    setKeepHover={setKeepHover}
                    onVideoOpen={onVideoOpen}
                />
            </ControlsContainer>
        );

    return (
        <Container>
            <ImageContainer
                data-testid={`translation-card-image-container-${translationGroup.id}`}
                onClick={isSelectable ? handleSelect : undefined}
                isItemSelected={isItemSelected}
                isSelectable={isSelectable}
                disableHover={isLoading || isUploading}
                hovered={(isError || isDone || isDraftEditable) && keepHover}>
                <BackgroundOverlay bg={translationGroup.thumbnail_url} isLoading={!(isDone || isDraftEditable)} disabled={isError} />
                {middleContainer()}
                {controlsContainer()}
                {isDone && isExample && <ExampleBadge>Example</ExampleBadge>}
                {badgeContainer()}
            </ImageContainer>
            {infoContainer()}
        </Container>
    );
};

const TranslationGroupCardErrorBoundary = (props: Props): ReactElement => (
    <ErrorBoundary
        fallback={<TranslationGroupCard {...props} />}
        beforeCapture={scope =>
            scope.setTags({ component: 'TranslationGroupContainer', id: props.translationGroup.id, status: props.translationGroup.status })
        }>
        <TranslationGroupCard {...props} />
    </ErrorBoundary>
);

export { TranslationGroupCardErrorBoundary as TranslationGroupCard };
